/**
 * DocumentPreviewDrawer — side drawer with an embedded document preview
 * (Brief 91 + bundle-builder reliability follow-up).
 *
 * Fetches an ORDERED LIST of candidate preview URLs from
 * POST /api/documents/preview on open (best tier first — Graph /preview
 * embed, then downloadUrl, then PDF web viewer, then bare webUrl). Renders
 * the first tier in an iframe; if that iframe fails to load (onError, or a
 * load timeout because some SharePoint failures render a same-origin error
 * page without ever firing onError), it silently advances to the next tier.
 *
 * Why: Graph's /preview getUrl frequently 404s inside SharePoint's own
 * embed.aspx viewer even when Graph returned 200 for it — a failure that
 * only ever shows up once the iframe tries to load, so the backend can't
 * pre-empt it. Falling through tiers client-side means a flaky tier 1 never
 * strands the user on a dead "File Not Found" page.
 *
 * BM-H3887: before any SharePoint tier, the drawer asks the backend for the
 * document rendered as a PDF by the Bundle Builder's own converter
 * (POST /api/documents/preview/rendered) and shows that. SharePoint cannot
 * show a saved email inline at all (its embed refuses .eml/.msg and the
 * download URL makes the browser save the file), so emails previewed as a
 * blank pane. The rendered page is also exactly what the bundle will carry,
 * which is the point of checking it for privileged material before adding
 * it. If that call fails (an older backend, a file the converter cannot
 * read) the SharePoint tiers below take over as before.
 *
 * Closes on Esc or backdrop click.
 */
import { useEffect } from "react";
import { X, ExternalLink, ChevronLeft, ChevronRight, Plus, Check } from "lucide-react";
import DocumentPreviewBody from "./DocumentPreviewBody";

// BM-H4266: the preview itself (tiers, iframe, fallbacks) moved to
// DocumentPreviewBody so the matter page's documents pane shows documents
// the same way. This drawer is the chrome around it. ``strategy`` is
// DocumentPreviewBody's: "bundle" (default, Finder and Bundle Builder) or
// "matter" (File Snap rows, the matter page).
function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

// 5 Nov 2026 review: the drawer was 672px wide (an A4 page shrank to unreadable
// type), said nothing about where the file sits or when it was saved, and to
// look at the next document the drawer had to be closed and the next row found.
// It is now wider, shows the folder and date, and steps through the results
// (buttons or the left/right arrow keys). onPrev/onNext/onAddToBundle are
// optional; without them the drawer is as before.
export default function DocumentPreviewDrawer({
  doc, onClose, strategy = "bundle", onPrev, onNext, position, onAddToBundle, inBundle,
}) {
  const open = !!doc;

  // Esc closes the drawer; the arrow keys step through the results.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") { if (onClose) onClose(); return; }
      const tag = (e.target && e.target.tagName) || "";
      if (/^(INPUT|TEXTAREA|SELECT)$/.test(tag)) return;
      if (e.key === "ArrowLeft" && onPrev) onPrev();
      if (e.key === "ArrowRight" && onNext) onNext();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, onPrev, onNext]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="doc-preview-drawer">
      <div
        className="absolute inset-0 bg-black/50"
        data-testid="doc-preview-backdrop"
        onClick={() => onClose && onClose()}
      />
      <div className="relative w-full max-w-4xl xl:max-w-[60vw] h-full bg-navy-card border-l border-navy-border shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-navy-border">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-white truncate" title={doc.title}>
              {doc.title || "Preview"}
            </h2>
            {doc.matter_ref && (
              <div className="text-xs text-teal truncate">{doc.matter_ref}</div>
            )}
            {((doc.breadcrumb || []).length > 0 || doc.modified_at) && (
              <div
                className="text-[11px] text-slate-400 truncate"
                data-testid="doc-preview-location"
                title={(doc.breadcrumb || []).join(" / ")}
              >
                {(doc.breadcrumb || []).slice(-2).join(" / ")}
                {(doc.breadcrumb || []).length > 0 && doc.modified_at ? " · " : ""}
                {formatDate(doc.modified_at)}
                {doc.modified_by ? ` · ${doc.modified_by}` : ""}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {(onPrev || onNext) && (
              <div className="flex items-center gap-1 text-xs text-slate-400">
                <button
                  type="button"
                  data-testid="doc-preview-prev"
                  onClick={() => onPrev && onPrev()}
                  disabled={!onPrev}
                  aria-label="Previous document"
                  className="p-1 rounded-md hover:text-white hover:bg-slate-700 disabled:opacity-30 disabled:hover:bg-transparent"
                >
                  <ChevronLeft size={16} />
                </button>
                {position && <span data-testid="doc-preview-position">{position}</span>}
                <button
                  type="button"
                  data-testid="doc-preview-next"
                  onClick={() => onNext && onNext()}
                  disabled={!onNext}
                  aria-label="Next document"
                  className="p-1 rounded-md hover:text-white hover:bg-slate-700 disabled:opacity-30 disabled:hover:bg-transparent"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            )}
            {onAddToBundle && (
              <button
                type="button"
                data-testid="doc-preview-bundle"
                onClick={() => onAddToBundle(doc)}
                disabled={inBundle}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs border ${
                  inBundle
                    ? "bg-teal/20 text-teal border-teal cursor-default"
                    : "bg-slate-800 text-slate-200 border-navy-border hover:bg-slate-700"
                }`}
              >
                {inBundle ? <><Check size={13} /> In bundle</> : <><Plus size={13} /> Add to bundle</>}
              </button>
            )}
            {doc.web_url && (
              <a
                href={doc.web_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="doc-preview-open"
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs
                  bg-slate-800 text-slate-200 border border-navy-border hover:bg-slate-700"
              >
                <ExternalLink size={13} /> Open
              </a>
            )}
            <button
              type="button"
              data-testid="doc-preview-close"
              onClick={() => onClose && onClose()}
              className="p-1 rounded-md text-slate-400 hover:text-white hover:bg-slate-700"
              aria-label="Close preview"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <DocumentPreviewBody
          doc={doc}
          strategy={strategy}
          renderedNote={strategy === "bundle" ? "Shown as it will appear in the bundle." : undefined}
        />
      </div>
    </div>
  );
}
